import type { FederatedPointerEvent } from 'pixi.js';
import { Container, Graphics, Text } from 'pixi.js';
import type { CoachStep } from '../coach.js';
import { COLORS, DISPLAY_FONT } from '../theme.js';

/**
 * What a lesson looks like (ADR-0030): the screen dims, what the lesson is
 * about stays lit, a bubble says it in a line or two, and, when there is a
 * floor to tap, that floor's whole row breathes and a hand taps it.
 *
 * The row is the target, not an arrow beside it: in beta 2 the old arrow
 * pointed into the shaft, and newcomers tapped where it pointed.
 *
 * While shown it takes every tap. A lesson with a floor takes only a tap on
 * that floor; one without takes any tap to go on. The caller decides what a
 * tap means; this only says where it landed.
 */

export interface Box {
  readonly x: number;
  readonly y: number;
  readonly w: number;
  readonly h: number;
}

export interface CoachLayout {
  readonly width: number;
  readonly height: number;
  /** What is lit. */
  readonly holes: readonly Box[];
  /** The row to tap, if there is one. */
  readonly target: Box | null;
  /** Where on the row the hand taps. */
  readonly touch: { readonly x: number; readonly y: number } | null;
}

const BUBBLE_PAD = 14;
const BUBBLE_MAX = 300;
const MARGIN = 16;
/** One tap of the hand, in ms: down, a ripple, back up. */
const TAP_CYCLE_MS = 1200;

export class CoachOverlay {
  readonly layer = new Container();
  private readonly dim = new Graphics();
  private readonly ring = new Graphics();
  private readonly hand = new Graphics();
  private readonly bubble = new Graphics();
  private readonly words: Text;
  private readonly prompt: Text;
  private readonly catcher = new Graphics();
  private step: CoachStep | null = null;
  private layout: CoachLayout | null = null;

  /** A tap while a lesson is shown: where, in stage coordinates. */
  onTap: (x: number, y: number) => void = () => {};

  constructor(private readonly still: boolean) {
    this.words = new Text({
      text: '',
      style: {
        fontFamily: DISPLAY_FONT,
        fontWeight: '700',
        fontSize: 16,
        lineHeight: 21,
        fill: COLORS.ink,
        wordWrap: true,
        wordWrapWidth: BUBBLE_MAX - BUBBLE_PAD * 2,
      },
    });
    this.prompt = new Text({
      text: 'Tap anywhere to go on',
      style: { fontFamily: DISPLAY_FONT, fontWeight: '700', fontSize: 12, fill: COLORS.target },
    });
    this.catcher.eventMode = 'static';
    this.catcher.on('pointertap', (event: FederatedPointerEvent) => {
      this.onTap(event.global.x, event.global.y);
    });
    this.layer.addChild(
      this.dim,
      this.ring,
      this.catcher,
      this.bubble,
      this.words,
      this.prompt,
      this.hand,
    );
    this.layer.visible = false;
  }

  show(step: CoachStep | null): void {
    this.step = step;
    this.layer.visible = step !== null;
    if (step) this.words.text = step.lines.join('\n');
  }

  get showing(): CoachStep | null {
    return this.step;
  }

  /** Lay it out for this frame. `clock` drives the hand and the breathing row. */
  draw(layout: CoachLayout, clock: number): void {
    if (!this.step) return;
    this.layout = layout;
    const { width, height } = layout;

    this.dim.clear().rect(0, 0, width, height).fill({ color: COLORS.ink, alpha: 0.62 });
    for (const hole of mergeBoxes(layout.holes)) {
      this.dim.roundRect(hole.x, hole.y, hole.w, hole.h, 10).cut();
    }
    this.catcher.clear().rect(0, 0, width, height).fill({ color: 0xffffff, alpha: 0.001 });

    const phase = this.still ? 0.5 : (clock % TAP_CYCLE_MS) / TAP_CYCLE_MS;
    this.ring.clear();
    if (layout.target) {
      const t = layout.target;
      const breath = this.still ? 0.9 : 0.6 + 0.4 * Math.sin(phase * Math.PI * 2);
      this.ring
        .roundRect(t.x, t.y, t.w, t.h, 10)
        .stroke({ width: 3, color: COLORS.target, alpha: breath });
    }

    this.hand.clear();
    if (layout.touch) paintHand(this.hand, layout.touch.x, layout.touch.y, phase, this.still);

    this.placeBubble(layout);
  }

  /** Below what is lit if it fits there, otherwise above it; never over it. */
  private placeBubble(layout: CoachLayout): void {
    const { width, height, holes } = layout;
    const w = Math.min(BUBBLE_MAX, width - MARGIN * 2);
    this.words.style.wordWrapWidth = w - BUBBLE_PAD * 2;
    const tapAnywhere = this.step?.tap === null;
    this.prompt.visible = tapAnywhere;
    const h = this.words.height + BUBBLE_PAD * 2 + (tapAnywhere ? this.prompt.height + 6 : 0);

    const top = Math.min(...holes.map((b) => b.y));
    const bottom = Math.max(...holes.map((b) => b.y + b.h));
    const below = height - bottom;
    const above = top;
    const y =
      below >= h + MARGIN * 2
        ? bottom + MARGIN
        : above >= h + MARGIN * 2
          ? top - MARGIN - h
          : Math.max(MARGIN, (height - h) / 2);
    const x = (width - w) / 2;

    this.bubble
      .clear()
      .roundRect(x, y, w, h, 14)
      .fill({ color: COLORS.cabin })
      .stroke({ width: 2, color: COLORS.ink });
    this.words.position.set(x + BUBBLE_PAD, y + BUBBLE_PAD);
    this.prompt.position.set(x + BUBBLE_PAD, y + BUBBLE_PAD + this.words.height + 6);
  }

  /** Whether a tap at this height landed on the row to tap. */
  onTarget(y: number): boolean {
    const t = this.layout?.target;
    return t !== null && t !== undefined && y >= t.y && y <= t.y + t.h;
  }

  destroy(): void {
    this.layer.destroy({ children: true });
  }
}

/**
 * Lit areas that overlap become one: two cuts through the same spot leave a
 * stray wedge of light instead of a hole (a row and the people on it, say).
 */
export function mergeBoxes(boxes: readonly Box[]): Box[] {
  const out = [...boxes];
  for (let merged = true; merged; ) {
    merged = false;
    for (let i = 0; i < out.length && !merged; i += 1) {
      for (let j = i + 1; j < out.length && !merged; j += 1) {
        const a = out[i] as Box;
        const b = out[j] as Box;
        if (a.x < b.x + b.w && b.x < a.x + a.w && a.y < b.y + b.h && b.y < a.y + a.h) {
          const x = Math.min(a.x, b.x);
          const y = Math.min(a.y, b.y);
          const w = Math.max(a.x + a.w, b.x + b.w) - x;
          const h = Math.max(a.y + a.h, b.y + b.h) - y;
          out.splice(j, 1);
          out[i] = { x, y, w, h };
          merged = true;
        }
      }
    }
  }
  return out;
}

/**
 * A gloved hand, index finger up, its tip at (x, y): it presses, a ripple
 * spreads where it touched, and it lifts again. Held still, it rests on the
 * spot with no ripple.
 */
function paintHand(g: Graphics, x: number, y: number, phase: number, still: boolean): void {
  // Down over the first third, held, then back up.
  const press = still
    ? 1
    : phase < 0.3
      ? phase / 0.3
      : phase < 0.55
        ? 1
        : 1 - (phase - 0.55) / 0.45;
  const lift = (1 - press) * 12;
  if (!still && phase >= 0.3 && phase < 0.9) {
    const spread = (phase - 0.3) / 0.6;
    g.circle(x, y, 6 + spread * 18).stroke({
      width: 2.5,
      color: COLORS.cabin,
      alpha: 0.9 * (1 - spread),
    });
  }
  const tipY = y + lift;
  const line = { width: 1.8, color: COLORS.ink };
  // Thumb, palm with the folded fingers, then the pointing finger over them.
  g.ellipse(x - 9, tipY + 31, 5, 8)
    .fill({ color: COLORS.cabin })
    .stroke(line);
  g.roundRect(x - 7, tipY + 17, 25, 27, 9)
    .fill({ color: COLORS.cabin })
    .stroke(line);
  for (const k of [0, 1, 2]) {
    g.moveTo(x + 5 + k * 5, tipY + 19)
      .lineTo(x + 5 + k * 5, tipY + 25)
      .stroke({ ...line, width: 1.2 });
  }
  g.roundRect(x - 5, tipY, 10, 28, 5)
    .fill({ color: COLORS.cabin })
    .stroke(line);
  g.roundRect(x - 7, tipY + 42, 25, 6, 3)
    .fill({ color: COLORS.target })
    .stroke(line);
}
